import { useState, useMemo, useEffect, useRef } from 'react';
import { Search, X, Star, Clock, TrendingUp, Shield } from 'lucide-react';
import { useCurrencies } from '../hooks/useExchange';
import CurrencyIcon from './CurrencyIcon';
import './CurrencySelector.css';

const POPULAR_CURRENCIES = ['btc', 'eth', 'usdt', 'bnb', 'sol', 'ada', 'xrp', 'dot', 'doge', 'matic'];
const STABLECOINS = ['usdt', 'usdc', 'busd', 'dai', 'tusd'];

// Brand colors for popular coins
const coinColors = {
  btc: '#f7931a',
  eth: '#627eea',
  usdt: '#26a17b',
  bnb: '#f3ba2f',
  sol: '#9945ff',
  ada: '#0033ad',
  xrp: '#23292f',
  dot: '#e6007a',
  doge: '#c2a633',
  matic: '#8247e5',
  avax: '#e84142',
  ltc: '#bfbbbb',
  link: '#2a5ada',
  uni: '#ff007a',
  atom: '#2e3148',
  etc: '#328332',
  xlm: '#08b5e5',
  trx: '#ff060a',
  shib: '#e42e2e',
  fil: '#0090ff',
  vet: '#15bdff',
  xtz: '#2c7df7',
  eos: '#443f54',
  aave: '#b6509e',
  comp: '#00d395',
  mkr: '#1aab9b',
  snx: '#00d1ff',
  grt: '#6747ed',
  crv: '#00d1ff',
  '1inch': '#1c324f',
  lrc: '#1a5aff',
  zil: '#49c1bf',
  neo: '#58bf00',
  qtum: '#2e9ad0',
  dash: '#008de4',
  zec: '#f4b728',
  bch: '#8dc351',
  dai: '#f5ac37',
  busd: '#f0b90b',
  usdc: '#2775ca',
  tusd: '#2b2e7f',
};

const CurrencyIconWithFallback = ({ currency }) => {
  const [error, setError] = useState(false);
  
  if (error) {
    const color = coinColors[currency.ticker.toLowerCase()] || '#3b82f6';
    const initial = currency.ticker.charAt(0).toUpperCase();
    
    return (
      <div 
        className="currency-icon-fallback"
        style={{ backgroundColor: color }}
      >
        {initial}
      </div>
    );
  }
  
  return (
    <CurrencyIcon 
      currency={currency}
      size="md"
      lazy={true}
      onError={() => setError(true)}
    />
  );
};

const CurrencySelector = ({ onSelect, onClose, excludeCurrency, type }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [recentCurrencies, setRecentCurrencies] = useState([]);
  const { data: currencies = [], isLoading } = useCurrencies();
  const modalRef = useRef(null);

  // Load recent currencies from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentCurrencies');
    if (saved) {
      try {
        setRecentCurrencies(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse recent currencies', e);
      }
    }
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const filteredCurrencies = useMemo(() => {
    let filtered = currencies;

    // Exclude selected currency from opposite side
    if (excludeCurrency) {
      filtered = filtered.filter(
        (c) => c.ticker !== excludeCurrency.ticker || c.network !== excludeCurrency.network
      );
    }

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.ticker.toLowerCase().includes(query) ||
          c.network.toLowerCase().includes(query)
      );
    }

    // Apply tab filter
    switch (activeTab) {
      case 'popular':
        filtered = filtered.filter((c) => POPULAR_CURRENCIES.includes(c.ticker.toLowerCase()));
        break;
      case 'stable':
        filtered = filtered.filter((c) => STABLECOINS.includes(c.ticker.toLowerCase()));
        break;
      case 'recent':
        if (recentCurrencies.length > 0) {
          const recentTickers = recentCurrencies.map(c => c.ticker.toLowerCase());
          filtered = filtered.filter((c) => recentTickers.includes(c.ticker.toLowerCase()));
        }
        break;
      default:
        break;
    }

    return filtered;
  }, [currencies, excludeCurrency, searchQuery, activeTab, recentCurrencies]);

  const popularCurrenciesList = useMemo(() => {
    return currencies
      .filter((c) => POPULAR_CURRENCIES.includes(c.ticker.toLowerCase()))
      .slice(0, 8);
  }, [currencies]);

  const groupedCurrencies = useMemo(() => {
    const groups = {};
    filteredCurrencies.forEach((currency) => {
      const firstLetter = currency.name[0].toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(currency);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredCurrencies]);

  const handleSelect = (currency) => {
    // Save to recent
    const newRecent = [currency, ...recentCurrencies.filter(c => c.ticker !== currency.ticker)].slice(0, 5);
    setRecentCurrencies(newRecent);
    localStorage.setItem('recentCurrencies', JSON.stringify(newRecent));
    onSelect(currency);
  };

  return (
    <div className="currency-selector-overlay">
      <div className="currency-selector-modal" ref={modalRef}>
        <div className="selector-header">
          <h3 className="selector-title">
            Select {type === 'from' ? 'currency you send' : 'currency you get'}
          </h3>
          <button className="selector-close" onClick={onClose}>
            <X />
          </button>
        </div>

        <div className="selector-search">
          <Search className="search-icon" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search currency (e.g. BTC, Ethereum)"
            className="search-input"
            autoFocus
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <X className="clear-icon" />
            </button>
          )}
        </div>

        <div className="selector-tabs">
          <button
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All
          </button>
          <button
            className={`tab ${activeTab === 'popular' ? 'active' : ''}`}
            onClick={() => setActiveTab('popular')}
          >
            <Star className="tab-icon" />
            Popular
          </button>
          <button
            className={`tab ${activeTab === 'stable' ? 'active' : ''}`}
            onClick={() => setActiveTab('stable')}
          >
            Stable
          </button>
          {recentCurrencies.length > 0 && (
            <button
              className={`tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              <Clock className="tab-icon" />
              Recent
            </button>
          )}
        </div>

        <div className="selector-content">
          {!searchQuery && activeTab === 'all' && (
            <div className="popular-section">
              <span className="section-label">Popular currencies</span>
              <div className="popular-grid">
                {popularCurrenciesList.map((currency) => (
                  <button
                    key={currency.id}
                    className="popular-item"
                    onClick={() => handleSelect(currency)}
                  >
                    <CurrencyIconWithFallback currency={currency} />
                    <span className="popular-code">{currency.ticker.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="currency-list">
            {isLoading ? (
              <div className="loading-state">
                <div className="loading-spinner" />
                <span>Loading currencies...</span>
              </div>
            ) : filteredCurrencies.length === 0 ? (
              <div className="empty-state">
                <TrendingUp className="empty-icon" />
                <p>No currencies found</p>
                <span>Try a different search term</span>
              </div>
            ) : searchQuery || activeTab !== 'all' ? (
              // Flat list for search or filtered results
              filteredCurrencies.map((currency) => (
                <button
                  key={currency.id}
                  className="currency-row"
                  onClick={() => handleSelect(currency)}
                >
                  <CurrencyIconWithFallback currency={currency} />
                  <div className="currency-row-info">
                    <span className="currency-row-name">{currency.name}</span>
                    <div className="currency-row-meta">
                      <span className="network-badge-small">{currency.network}</span>
                      {STABLECOINS.includes(currency.ticker.toLowerCase()) && (
                        <span className="stable-badge">Stable</span>
                      )}
                    </div>
                  </div>
                  <span className="currency-row-ticker">{currency.ticker.toUpperCase()}</span>
                </button>
              ))
            ) : (
              // Grouped list for browsing
              groupedCurrencies.map(([letter, groupCurrencies]) => (
                <div key={letter} className="currency-group">
                  <div className="group-header">{letter}</div>
                  {groupCurrencies.map((currency) => (
                    <button
                      key={currency.id}
                      className="currency-row"
                      onClick={() => handleSelect(currency)}
                    >
                      <CurrencyIconWithFallback currency={currency} />
                      <div className="currency-row-info">
                        <span className="currency-row-name">{currency.name}</span>
                        <div className="currency-row-meta">
                          <span className="network-badge-small">{currency.network}</span>
                          {STABLECOINS.includes(currency.ticker.toLowerCase()) && (
                            <span className="stable-badge">Stable</span>
                          )}
                        </div>
                      </div>
                      <span className="currency-row-ticker">{currency.ticker.toUpperCase()}</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="selector-footer">
          <div className="supported-count">
            <Shield className="shield-icon" />
            <span>1000+ supported currencies</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrencySelector;
